import { useEffect, useMemo, useState } from 'react';
import { useGetAllStores, useGetStoreByPostal } from '../../api/store/query';
import { toOpenCloseDisplay } from '../../utils/store';

const matchStore = (store: IPanelStore, query: string) =>
  (store.address && store.address.toLowerCase().indexOf(query) >= 0) ||
  (store.city && store.city?.toLowerCase()?.indexOf(query) >= 0) ||
  store.name?.toLowerCase()?.indexOf(query) >= 0 ||
  (store.keywords && store.keywords.toLowerCase().indexOf(query) >= 0) ||
  false;

const sortStores = (a: IPanelStore, b: IPanelStore) => a.name.localeCompare(b.name, 'sv');

const mapStore = (store: IStore) =>
  ({
    ...store,
    name: store.storeName,
    id: store.storeId,
    openToday: toOpenCloseDisplay(store.openingHoursToday),
  }) as IPanelStore;

const mapAndSortStores = (stores?: IStore[]) => stores?.map(mapStore)?.sort(sortStores);

const trimWhitespaces = (value: string) => value.replace(' ', '');

const isPostal = (searchInput: string) => {
  const trimmedInput = trimWhitespaces(searchInput);
  return /^\d+$/.test(trimmedInput) && trimmedInput.length === 5;
};

const selectableQuery = (store: IPanelStore) => store?.webOrderAvailable && store?.hasOpened;

export default (
  loading?: boolean,
  allowedStoresFilter?: (store: IStore) => void,
  selectedStoreId?: string,
) => {
  const { data: allStores, isLoading: loadingStores } = useGetAllStores<IStore>();
  const allowedStores = useMemo(() => {
    return allowedStoresFilter ? allStores?.filter(allowedStoresFilter) : allStores;
  }, [allStores, allowedStoresFilter]);

  const [postalInput, setPostalInput] = useState('');
  const {
    data: { store: postalStore } = { store: undefined },
    isLoading: loadingPostalStores,
  } = useGetStoreByPostal(postalInput);

  const [mappedStores, setMappedStores] = useState(mapAndSortStores(allowedStores) || []);

  const handleSearch = (string: string) => {
    if (isPostal(string)) {
      setPostalInput(trimWhitespaces(string));
    } else {
      const filteredStores = mapAndSortStores(allowedStores)?.filter((store) =>
        matchStore(store, string.toLocaleLowerCase()),
      );

      setMappedStores(filteredStores || []);
      setPostalInput('');
    }
  };

  useEffect(() => {
    if (postalInput && !loadingPostalStores) {
      setMappedStores((postalStore && [mapStore(postalStore)]) || []);
    }
  }, [postalInput, postalStore, loadingPostalStores]);

  useEffect(() => {
    setMappedStores(mapAndSortStores(allowedStores) || []);
  }, [allowedStores]);

  const mappedAdjacentStores = useMemo(() => {
    const selectedStore = allStores?.find((s) => s.storeId === selectedStoreId);
    const adjacentUserStoreIds = selectedStore?.adjacentStores;

    return mappedStores.filter((mappedStore) => adjacentUserStoreIds?.includes(mappedStore.id));
  }, [mappedStores, allStores, selectedStoreId]);

  const showLoader = loadingStores || loadingPostalStores || loading;

  return { showLoader, mappedStores, mappedAdjacentStores, selectableQuery, handleSearch };
};
